import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, Mail, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { theme } from '../styles/theme';
import logoColor from '../assets/libben-smart.png';

export default function TermsPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('pt') ? 'pt-BR' : 'en';
  const { t } = useTranslation('terms', { lng: lang });
  const navigate = useNavigate();
  const location = useLocation();
  const fromRegister = location.state?.from === 'register';
  const sections = t('sections', { returnObjects: true });

  return (
    <Page>
      <Glow />

      <TopBar>
        <LogoArea>
          <LogoImg src={logoColor} alt="Libben Agents" />
        </LogoArea>
        <BackBtn aria-label="Voltar" onClick={() => fromRegister ? navigate(-1) : navigate('/login')}>
          <ArrowLeft size={15} /> Voltar
        </BackBtn>
      </TopBar>

      <Container>
        <Card>
          <CardHeader>
            <FileText size={22} color={theme.colors.primary} />
            <div>
              <CardTitle>{t('pageTitle')}</CardTitle>
              <CardMeta>
                <MetaBadge>{t('version')}</MetaBadge>
                <MetaDate>{t('lastUpdated')}</MetaDate>
              </CardMeta>
            </div>
          </CardHeader>

          <Divider />

          <Sections>
            {Array.isArray(sections) && sections.map((section, i) => (
              <Section key={i}>
                <SectionTitle>{section.title}</SectionTitle>
                <SectionText>{section.content}</SectionText>
              </Section>
            ))}
          </Sections>

          <Divider />

          <ContactBox>
            <ContactTitle>{t('contact.title')}</ContactTitle>
            <ContactGrid>
              <ContactItem>
                <Mail size={14} />
                <span>{t('contact.support')}</span>
                <a href="mailto:suporte@libben.com.br">suporte@libben.com.br</a>
              </ContactItem>
              <ContactItem>
                <Mail size={14} />
                <span>{t('contact.privacy')}</span>
                <a href="mailto:privacidade@libben.com.br">privacidade@libben.com.br</a>
              </ContactItem>
              <ContactItem>
                <Mail size={14} />
                <span>{t('contact.billing')}</span>
                <a href="mailto:financeiro@libben.com.br">financeiro@libben.com.br</a>
              </ContactItem>
            </ContactGrid>
          </ContactBox>

          <Footer>{t('copyright')}</Footer>
        </Card>
      </Container>
    </Page>
  );
}

const Page = styled.div`
  min-height: 100vh;
  background: ${theme.colors.gray900};
  position: relative;
  overflow-x: hidden;
`;

const Glow = styled.div`
  position: fixed;
  width: 700px;
  height: 700px;
  border-radius: 50%;
  background: radial-gradient(circle, ${theme.colors.primary}22 0%, transparent 70%);
  top: -200px;
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none;
  z-index: 0;
`;

const TopBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background: ${theme.colors.gray900}cc;
  backdrop-filter: blur(8px);
  border-bottom: 1px solid ${theme.colors.gray700};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
`;

const LogoArea = styled.div`display: flex; align-items: center;`;

const LogoImg = styled.img`
  height: 36px;
  width: auto;
  object-fit: contain;
`;

const BackBtn = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  background: none;
  border: 1px solid ${theme.colors.gray700};
  border-radius: ${theme.radii.md};
  color: ${theme.colors.gray400};
  font-size: ${theme.typography.sm};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  cursor: pointer;
  transition: ${theme.transitions.fast};
  &:hover {
    color: ${theme.colors.white};
    border-color: ${theme.colors.gray500};
    background: ${theme.colors.gray800};
  }
`;

const Container = styled.div`
  position: relative;
  z-index: 1;
  max-width: 800px;
  margin: 0 auto;
  padding: ${theme.spacing[8]} ${theme.spacing[4]};
`;

const Card = styled.div`
  background: ${theme.colors.white};
  border-radius: ${theme.radii['2xl']};
  padding: ${theme.spacing[8]};
  box-shadow: ${theme.shadows.xl};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  @media (max-width: 639px) {
    padding: ${theme.spacing[5]};
    border-radius: ${theme.radii.xl};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
`;

const CardTitle = styled.h1`
  font-size: ${theme.typography['2xl']};
  font-weight: ${theme.typography.bold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const MetaBadge = styled.span`
  padding: 2px ${theme.spacing[2]};
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primary};
  border: 1px solid ${theme.colors.primaryBorder};
  border-radius: ${theme.radii.full};
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.semibold};
`;

const MetaDate = styled.span`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textMuted};
`;

const Divider = styled.div`
  height: 1px;
  background: ${theme.colors.border};
`;

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const Section = styled.div`
  padding: ${theme.spacing[4]};
  background: ${theme.colors.gray50};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.lg};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.base};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const SectionText = styled.p`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textSecondary};
  line-height: 1.7;
  margin: 0;
`;

const ContactBox = styled.div`
  padding: ${theme.spacing[4]};
  background: ${theme.colors.primaryLight};
  border: 1px solid ${theme.colors.primaryBorder};
  border-radius: ${theme.radii.lg};
`;

const ContactTitle = styled.h3`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.primary};
  margin: 0 0 ${theme.spacing[3]} 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ContactGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textSecondary};
  svg { flex-shrink: 0; color: ${theme.colors.primary}; }
  a {
    color: ${theme.colors.primary};
    font-weight: ${theme.typography.medium};
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`;

const Footer = styled.div`
  text-align: center;
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
`;
